import React, { useCallback, useRef } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  GoogleReCaptchaCheckbox,
  GoogleReCaptchaProvider
} from '@google-recaptcha/react';
import {
  CheckBox,
  ErrorSpan,
  FormCaption,
  FormSubmit,
  InformSpan,
  LinkText
} from '../../common/components';
import { Input } from '../../common/components/form/input.component';
import {
  AUTH_PATTERN,
  AUTH_VALIDATION_CONFIG,
  EmailErrors,
  PasswordErrors,
  TermsErrors
} from './validation.pattern';
import { APP_KEYS } from '../../common/consts';
import { ROUTER_LINKS, TERMS_VERSION } from '../../common/consts/app-keys.const';
import { ISignup } from '../types';
import { authService } from '../service/auth.service';
import { useThrottle } from '../../hooks/useThrottle';
import { LockInputIcon } from '../../common/components/icons/lock-input-icon.component';
import { EmailInputIcon } from '../../common/components/icons/email-input-icon.component';
import useAuth from '../feature/auth';

type Inputs = {
  email: string;
  password: string;
  terms: any;
};

export const Signup = () => {
  const { t } = useTranslation();
  useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const trialProduct = searchParams.get('trial');
  const [isTermsAccepted, setIsTermsAccepted] = React.useState<boolean | null>(null);
  const [isRecaptchaError, setIsRecaptchaError] = React.useState(false);
  const [isRecaptchaTouch, setIsRecaptchaTouch] = React.useState<boolean | null>(
    null
  );
  // The token is the whole point: it is what the server checks with Google.
  // Holding only a "was it touched" boolean is what made this widget
  // decorative for three years.
  const [recaptchaToken, setRecaptchaToken] = React.useState<string | null>(null);

  const recaptcaErrorHandler = useCallback(() => {
    setIsRecaptchaError(true);
    setIsRecaptchaTouch(true);
    // An expired or failed challenge must invalidate the token it issued,
    // otherwise submit would still carry a stale one.
    setRecaptchaToken(null);
  }, []);

  const recaptchaChangeHandler = useCallback((token: string | null) => {
    setIsRecaptchaError(false);
    setIsRecaptchaTouch(true);
    setRecaptchaToken(token);
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isValid }
  } = useForm<Inputs>({
    mode: 'onChange'
  });

  React.useEffect(() => {
    if (watch('terms') === true) {
      setIsTermsAccepted(true);
    }
  }, [watch('terms')]);

  const signup = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.USER],
    mutationFn: async (data: ISignup) => authService.signup(data),
    onSuccess: async () => {
      navigate(APP_KEYS.ROUTER_LINKS.ACTIVATE_ACCOUNT, {
        state: { email: watch('email'), trial: trialProduct }
      });
    },

    onError: (err: any) => {
      const message = err.response?.data?.message ?? '';
      if (['Credentials taken', 'User already exists'].includes(message)) {
        setError('email', {
          type: 'manual',
          message: t('auth.accountExists')
        });
      } else if (message) {
        setError('root', {
          type: 'manual',
          message
        });
      } else {
        setError('root', {
          type: 'manual',
          message: t('auth.networkError')
        });
      }
    }
  });
  const throttledSignup = useThrottle(signup.mutate);

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    // Gate on the token, not on whether the widget was interacted with: an
    // errored or expired challenge sets touched=true but leaves no token, and
    // used to sail straight through.
    if (!recaptchaToken) {
      setIsRecaptchaTouch(false);
      return;
    }

    if (data.terms !== true) {
      setIsTermsAccepted(false);
      return;
    }
    throttledSignup({
      email: data.email,
      password: data.password,
      terms: true,
      termsVersion: TERMS_VERSION,
      recaptchaToken
    });
  };

  return (
    <>
      <FormCaption>{t('auth.signupTitle')}</FormCaption>
      {searchParams.get('reason') === 'download' ? (
        <div className="bg-blue-50 border border-blue-200 rounded-[10px] px-[16px] py-[12px] mb-[16px] text-[13px] text-blue-800">
          {t('auth.createFreeAccount')}
        </div>
      ) : (
        <InformSpan> {t('auth.createAccountAccess')} </InformSpan>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <Input
          errors={errors}
          name="email"
          label={t('auth.email')}
          placeholder={t('auth.enterHere')}
          type="email"
          icon={<EmailInputIcon />}
          register={register.bind(null, 'email', {
            required: { message: EmailErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.email,
              message: EmailErrors.pattern
            }
          })}
        />
        <Input
          errors={errors}
          name="password"
          label={t('auth.password')}
          placeholder={t('auth.enterHere')}
          type="password"
          icon={<LockInputIcon />}
          register={register.bind(null, 'password', {
            required: { message: PasswordErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.password,
              message: PasswordErrors.pattern
            },
            minLength: {
              value: AUTH_VALIDATION_CONFIG.password.minLength,
              message: PasswordErrors.minLength
            },
            maxLength: {
              value: AUTH_VALIDATION_CONFIG.password.maxLength,
              message: PasswordErrors.maxLength
            }
          })}
        />
        <div className="relative my-1 w-fit google-recaptcha-container">
          <GoogleReCaptchaCheckbox
            onChange={recaptchaChangeHandler}
            onError={recaptcaErrorHandler}
            expiredCallback={recaptcaErrorHandler}
          />
          {(isRecaptchaError || isRecaptchaTouch === false) && (
            <ErrorSpan>{t('auth.robotConfirm')}</ErrorSpan>
          )}
        </div>

        <div className="flex justify-between items-center max-w-[392px] my-[22px]">
          <CheckBox
            description={t('auth.agreeTermsPrivacy')}
            register={register.bind(null, 'terms')}
            terms
          />
          {isTermsAccepted === false && (
            <ErrorSpan>{TermsErrors.required}</ErrorSpan>
          )}
        </div>

        {errors.root && (
          <p className="text-red-500 font-body text-[14px] mb-4">
            {errors.root.message}
          </p>
        )}

        <FormSubmit
          className="mt-[2px]"
          isLoading={signup.isPending}
          isDisabled={!isValid || isRecaptchaTouch !== true || isRecaptchaError}
          value={t('auth.signupButton')}
        />

        <LinkText
          linkText={t('auth.loginButton')}
          to={ROUTER_LINKS.SIGNIN}
          text={t('auth.alreadyHaveAccount')}
        />
      </form>
    </>
  );
};
